/**
 * Path builders — the one place that knows the URL scheme (N3).
 * Keep in step with the route tree in ./router.tsx.
 */
import type { AdminArea } from './auth';

const seg = (s: string): string => encodeURIComponent(s);

export const paths = {
  home: () => '/',
  login: (redirectTo?: string | null) => (redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'),
  newOrder: (slug: string) => `/${seg(slug)}/orders/new`,
  order: (slug: string, orderNo: string) => `/${seg(slug)}/orders/${seg(orderNo)}`,
  newQuote: (slug: string) => `/${seg(slug)}/quotes/new`,
  quote: (slug: string, quoteNo: string) => `/${seg(slug)}/quotes/${seg(quoteNo)}`,
  invoice: (slug: string, invoiceNo: string, orderNo?: string | null) =>
    `/${seg(slug)}/invoices/${seg(invoiceNo)}${orderNo ? `?order=${encodeURIComponent(orderNo)}` : ''}`,
  creditMemo: (slug: string, no: string) => `/${seg(slug)}/credit-memos/${seg(no)}`,
  shipment: (slug: string, no: string) => `/${seg(slug)}/shipments/${seg(no)}`,
  settings: (slug: string) => `/${seg(slug)}/settings`,
  myAccount: (slug: string) => `/${seg(slug)}/my-account`,
  support: (slug: string) => `/${seg(slug)}/support`,
  /** UX-GROWTH H2: help centre (public, no company slug). */
  help: (article?: string | null) => (article ? `/help/${seg(article)}` : '/help'),
  admin: (area: AdminArea, tab?: string | null) => (tab && tab !== 'dashboard' ? `/${area}/${seg(tab)}` : `/${area}`),
  /** ADMIN-REVIEW X1: record detail page, e.g. /tntadm/users/{id}. */
  adminRecord: (area: AdminArea, tab: string, id: string) => `/${area}/${seg(tab)}/${seg(id)}`,
};

/**
 * Legacy page id → path, for the many `setCurrentPage('settings')`-style callers.
 * Pages that need a record id ('credit-memo-detail', 'shipment-detail') fall back to home.
 */
export function pageToPath(page: string, slug: string): string {
  switch (page) {
    case 'login': return paths.login();
    case 'home':
    case 'dashboard': return paths.home();
    case 'order-form': return paths.newOrder(slug);
    case 'settings': return paths.settings(slug);
    case 'my-account': return paths.myAccount(slug);
    case 'support-tickets': return paths.support(slug);
    case 'glbadm':
    case 'rsladm':
    case 'tntadm': return paths.admin(page);
    case 'admin': return '/admin';
    case 'docs-setup': return '/docs/setup';
    case 'help': return paths.help();
    case 'downloads': return '/downloads';
    case 'access-request': return '/access-request';
    case 'set-password': return '/set-password';
    case 'reseller-register': return '/reseller-register';
    case 'shell-preview': return '/shell-preview';
    default: return paths.home();
  }
}

/** First URL segment if it is a company slug (not a reserved top-level path). */
const RESERVED = new Set(['login', 'dashboard', 'docs', 'help', 'legal', 'downloads', 'access-request', 'set-password', 'reset-password',
  'reseller-register', 'shell-preview', 'admin', 'glbadm', 'rsladm', 'tntadm', 'api']);
export function slugFromPathname(pathname: string): string | null {
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first) {return null;}
  let decoded: string;
  try { decoded = decodeURIComponent(first); } catch { return null; }
  return RESERVED.has(decoded.toLowerCase()) ? null : decoded;
}
